import {
  AlertTriangle,
  ArrowRightLeft,
  BarChart3,
  Bell,
  CheckCircle2,
  ChevronDown,
  ClipboardCheck,
  Clock3,
  CloudOff,
  HardDrive,
  LayoutDashboard,
  LogOut,
  Menu,
  QrCode,
  ScanLine,
  School,
  Settings,
  UserCircle,
  Users,
  X,
  type LucideIcon,
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { todayISO } from "../lib/utils";
import { AttendancePage } from "../pages/AttendancePage";
import { ClassesPage } from "../pages/ClassesPage";
import { DashboardPage } from "../pages/DashboardPage";
import { ProfilePage } from "../pages/ProfilePage";
import { ReportsPage } from "../pages/ReportsPage";
import { ScannerPage } from "../pages/ScannerPage";
import { SettingsPage } from "../pages/SettingsPage";
import { StudentsPage } from "../pages/StudentsPage";
import { useApp } from "../state/AppContext";
import type { PageId, Role } from "../types";
import { Avatar, SchoolMark } from "./ui";

interface NavigationItem {
  id: PageId;
  label: string;
  mobileLabel?: string;
  icon: LucideIcon;
  roles: Role[];
}

const navigationItems: NavigationItem[] = [
  {
    id: "dashboard",
    label: "Utama",
    icon: LayoutDashboard,
    roles: ["admin", "teacher"],
  },
  {
    id: "attendance",
    label: "Kehadiran",
    icon: ClipboardCheck,
    roles: ["admin", "teacher"],
  },
  {
    id: "students",
    label: "Murid",
    icon: Users,
    roles: ["admin", "teacher"],
  },
  {
    id: "classes",
    label: "Kelas",
    icon: School,
    roles: ["admin"],
  },
  {
    id: "reports",
    label: "Laporan",
    icon: BarChart3,
    roles: ["admin"],
  },
  {
    id: "settings",
    label: "Tetapan",
    icon: Settings,
    roles: ["admin"],
  },
];

const mobileNavigationItems: NavigationItem[] = [
  navigationItems[0],
  navigationItems[1],
  {
    id: "scan",
    label: "Imbas QR",
    mobileLabel: "Imbas",
    icon: ScanLine,
    roles: ["teacher"],
  },
  navigationItems[2],
  {
    id: "profile",
    label: "Profil",
    mobileLabel: "Saya",
    icon: UserCircle,
    roles: ["admin", "teacher"],
  },
];

const pageLabels: Record<PageId, string> = {
  dashboard: "Utama",
  scan: "Imbas QR",
  attendance: "Kehadiran",
  students: "Murid",
  classes: "Kelas",
  reports: "Laporan",
  settings: "Tetapan",
  profile: "Profil",
};

const allPages = new Set<PageId>([
  "dashboard",
  "scan",
  "attendance",
  "students",
  "classes",
  "reports",
  "settings",
  "profile",
]);

const rolePages: Record<Role, Set<PageId>> = {
  admin: new Set<PageId>([
    "dashboard",
    "scan",
    "attendance",
    "students",
    "classes",
    "reports",
    "settings",
    "profile",
  ]),
  teacher: new Set<PageId>(["dashboard", "scan", "attendance", "students", "profile"]),
};

const pageFromHash = (): PageId => {
  const value = window.location.hash.replace(/^#\/?/, "") as PageId;
  return allPages.has(value) ? value : "dashboard";
};

const pageHash = (page: PageId) => `#/${page}`;

interface ShellNotification {
  id: string;
  title: string;
  description: string;
  icon: LucideIcon;
  tone: "success" | "warning" | "info" | "neutral";
  page?: PageId;
}

function SidebarNavigationButton({
  item,
  active,
  onSelect,
}: {
  item: NavigationItem;
  active: boolean;
  onSelect: (page: PageId) => void;
}) {
  const Icon = item.icon;
  return (
    <button
      type="button"
      className="sidebar-nav__item"
      aria-current={active ? "page" : undefined}
      aria-label={item.label}
      title={item.label}
      onClick={() => onSelect(item.id)}
    >
      <Icon size={20} aria-hidden="true" />
      <span>{item.label}</span>
    </button>
  );
}

export function AppShell() {
  const {
    session,
    students,
    attendance,
    login,
    logout,
  } = useApp();
  const [page, setPage] = useState<PageId>(pageFromHash);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [notificationsSeen, setNotificationsSeen] = useState(false);
  const [guardNotice, setGuardNotice] = useState("");
  const [online, setOnline] = useState(() => navigator.onLine);
  const mainRef = useRef<HTMLElement>(null);
  const popoverAreaRef = useRef<HTMLDivElement>(null);

  const role = session?.role ?? "teacher";
  const allowedPages = rolePages[role];
  const visibleNavigation = navigationItems.filter((item) => item.roles.includes(role));

  const navigate = useCallback(
    (requestedPage: PageId) => {
      const nextPage = allowedPages.has(requestedPage) ? requestedPage : "dashboard";
      if (nextPage !== requestedPage) {
        setGuardNotice(
          "Halaman itu hanya tersedia untuk Admin Sekolah. Anda dibawa semula ke Utama.",
        );
      }
      if (page !== nextPage) {
        window.history.pushState({ page: nextPage }, "", pageHash(nextPage));
      }
      setPage(nextPage);
      setDrawerOpen(false);
      setNotificationsOpen(false);
      setAccountOpen(false);
    },
    [allowedPages, page],
  );

  useEffect(() => {
    if (!window.location.hash) {
      window.history.replaceState({ page }, "", pageHash(page));
    }
  }, [page]);

  useEffect(() => {
    if (allowedPages.has(page)) return;
    setPage("dashboard");
    setGuardNotice(
      "Akses diselaraskan mengikut peranan Guru Kelas. Halaman pentadbir telah ditutup.",
    );
    window.history.replaceState({ page: "dashboard" }, "", pageHash("dashboard"));
  }, [allowedPages, page]);

  useEffect(() => {
    const onPopState = () => {
      const requestedPage = pageFromHash();
      if (!allowedPages.has(requestedPage)) {
        setGuardNotice(
          "Halaman itu hanya tersedia untuk Admin Sekolah. Anda dibawa semula ke Utama.",
        );
        setPage("dashboard");
        window.history.replaceState({ page: "dashboard" }, "", pageHash("dashboard"));
        return;
      }
      setPage(requestedPage);
      setDrawerOpen(false);
      setNotificationsOpen(false);
      setAccountOpen(false);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [allowedPages]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    window.requestAnimationFrame(() => mainRef.current?.focus({ preventScroll: true }));
  }, [page]);

  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setDrawerOpen(false);
      setNotificationsOpen(false);
      setAccountOpen(false);
    };
    const handleOutsideClick = (event: PointerEvent) => {
      if (
        popoverAreaRef.current &&
        !popoverAreaRef.current.contains(event.target as Node)
      ) {
        setNotificationsOpen(false);
        setAccountOpen(false);
      }
    };
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("pointerdown", handleOutsideClick);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("pointerdown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  const notifications = useMemo<ShellNotification[]>(() => {
    const activeStudents = students.filter((student) => student.active);
    const scopedStudents =
      role === "teacher"
        ? activeStudents.filter((student) => student.classId === session?.assignedClassId)
        : activeStudents;
    const scopedIds = new Set(scopedStudents.map((student) => student.id));
    const recordsToday = attendance.filter(
      (record) => record.date === todayISO() && scopedIds.has(record.studentId),
    );
    const recordedIds = new Set(recordsToday.map((record) => record.studentId));
    const remaining = scopedStudents.filter((student) => !recordedIds.has(student.id)).length;
    const late = recordsToday.filter((record) => record.status === "late").length;

    const firstNotification: ShellNotification =
      remaining > 0
        ? {
            id: "remaining",
            title: `${remaining} murid belum direkod`,
            description:
              role === "teacher"
                ? "Semak senarai kelas 3 Amanah sebelum sesi pagi tamat."
                : "Buka rekod hari ini untuk melihat murid dan kelas yang belum lengkap.",
            icon: AlertTriangle,
            tone: "warning",
            page: "attendance",
          }
        : {
            id: "complete",
            title: "Rekod hari ini lengkap",
            description:
              role === "teacher"
                ? "Semua murid aktif dalam kelas anda telah direkodkan."
                : "Semua murid aktif dalam skop demo telah direkodkan.",
            icon: CheckCircle2,
            tone: "success",
            page: "attendance",
          };

    const secondNotification: ShellNotification =
      late > 0
        ? {
            id: "late",
            title: `${late} rekod lewat hari ini`,
            description: "Status lewat boleh disemak dalam senarai Kehadiran.",
            icon: Clock3,
            tone: "info",
            page: "attendance",
          }
        : {
            id: "scan",
            title: "Pengimbas QR sedia digunakan",
            description: "Kamera, simulasi dan kemasukan kod manual tersedia dalam demo.",
            icon: QrCode,
            tone: "info",
            page: "scan",
          };

    return [
      firstNotification,
      secondNotification,
      {
        id: "local",
        title: "Demo disimpan pada peranti",
        description: "Tiada server sekolah atau penyegerakan akaun sebenar disambungkan.",
        icon: HardDrive,
        tone: "neutral",
      },
    ];
  }, [attendance, role, session?.assignedClassId, students]);

  const operationalUnread = notifications.filter(
    (notification) => notification.tone === "warning" || notification.tone === "info",
  ).length;
  const unreadCount = notificationsSeen ? 0 : operationalUnread;

  if (!session) return null;

  const switchRole = () => {
    const nextRole: Role = session.role === "admin" ? "teacher" : "admin";
    login(nextRole);
    setPage("dashboard");
    setGuardNotice("");
    window.history.pushState({ page: "dashboard" }, "", pageHash("dashboard"));
    setAccountOpen(false);
  };

  const signOut = () => {
    logout();
    window.history.replaceState(
      {},
      "",
      `${window.location.pathname}${window.location.search}`,
    );
  };

  let pageContent: ReactNode;
  switch (page) {
    case "scan":
      pageContent = <ScannerPage />;
      break;
    case "attendance":
      pageContent = <AttendancePage />;
      break;
    case "students":
      pageContent = <StudentsPage />;
      break;
    case "classes":
      pageContent = <ClassesPage />;
      break;
    case "reports":
      pageContent = <ReportsPage />;
      break;
    case "settings":
      pageContent = <SettingsPage />;
      break;
    case "profile":
      pageContent = <ProfilePage />;
      break;
    case "dashboard":
    default:
      pageContent = <DashboardPage onNavigate={navigate} />;
      break;
  }

  const dateLabel = new Intl.DateTimeFormat("ms-MY", {
    weekday: "short",
    day: "numeric",
    month: "short",
  }).format(new Date());

  return (
    <div className={`app-shell app-shell--${session.role}`}>
      <button
        type="button"
        className={`sidebar-scrim ${drawerOpen ? "sidebar-scrim--visible" : ""}`}
        aria-label="Tutup menu navigasi"
        tabIndex={drawerOpen ? 0 : -1}
        onClick={() => setDrawerOpen(false)}
      />

      <aside
        id="app-primary-navigation"
        className={`app-sidebar ${drawerOpen ? "app-sidebar--open" : ""}`}
        aria-label="Navigasi utama"
      >
        <div className="app-sidebar__brand">
          <SchoolMark inverse />
          <button
            type="button"
            className="app-sidebar__close"
            aria-label="Tutup menu"
            onClick={() => setDrawerOpen(false)}
          >
            <X size={21} />
          </button>
        </div>

        <div className="app-sidebar__body">
          <button
            type="button"
            className="sidebar-scan-action"
            aria-current={page === "scan" ? "page" : undefined}
            onClick={() => navigate("scan")}
          >
            <span className="sidebar-scan-action__icon" aria-hidden="true">
              <ScanLine size={20} />
            </span>
            <span>
              <strong>Imbas QR</strong>
              <small>Rekod kehadiran</small>
            </span>
          </button>

          <div className="app-sidebar__section-label">Ruang kerja</div>
          <nav className="sidebar-nav" aria-label="Halaman aplikasi">
            {visibleNavigation.map((item) => (
              <SidebarNavigationButton
                key={item.id}
                item={item}
                active={page === item.id}
                onSelect={navigate}
              />
            ))}
          </nav>
        </div>

        <footer className="app-sidebar__footer">
          <button
            type="button"
            className="sidebar-profile"
            aria-current={page === "profile" ? "page" : undefined}
            aria-label={`Buka profil ${session.name}`}
            title="Profil dan akaun demo"
            onClick={() => navigate("profile")}
          >
            <Avatar initials={session.initials} size="sm" label={session.name} />
            <span className="sidebar-profile__copy">
              <strong>{session.name}</strong>
              <small>{session.title}</small>
            </span>
            <ChevronDown className="sidebar-profile__chevron" size={16} aria-hidden="true" />
          </button>
          <div className="app-sidebar__demo">
            <CloudOff size={14} aria-hidden="true" />
            <span>Prototaip demo tempatan</span>
          </div>
        </footer>
      </aside>

      <div className="app-shell__workspace">
        <header className="app-topbar">
          <div className="app-topbar__left">
            <button
              type="button"
              className="topbar-icon-button topbar-menu-trigger"
              aria-label="Buka menu navigasi"
              aria-controls="app-primary-navigation"
              aria-expanded={drawerOpen}
              onClick={() => setDrawerOpen(true)}
            >
              <Menu size={21} />
            </button>

            <div className="app-topbar__context">
              <span>{session.role === "teacher" ? "Kelas 3 Amanah" : dateLabel}</span>
              <strong>{pageLabels[page]}</strong>
            </div>
          </div>

          <div className="app-topbar__actions" ref={popoverAreaRef}>
            <div
              className={`local-demo-indicator ${online ? "" : "local-demo-indicator--offline"}`}
              title={
                online
                  ? "Data demo disimpan pada pelayar; tiada server sekolah disambungkan."
                  : "Peranti sedang luar talian. Data demo kekal pada pelayar ini."
              }
            >
              <CloudOff size={15} aria-hidden="true" />
              <span>{online ? "Demo tempatan" : "Luar talian"}</span>
            </div>

            <div className="topbar-popover-anchor">
              <button
                type="button"
                className="topbar-icon-button"
                aria-label={
                  unreadCount > 0
                    ? `Buka makluman, ${unreadCount} belum dibaca`
                    : "Buka makluman"
                }
                aria-expanded={notificationsOpen}
                onClick={() => {
                  setNotificationsOpen((open) => !open);
                  setAccountOpen(false);
                  setNotificationsSeen(true);
                }}
              >
                <Bell size={20} />
                {unreadCount > 0 && (
                  <span className="topbar-notification-badge" aria-hidden="true">
                    {unreadCount}
                  </span>
                )}
              </button>

              {notificationsOpen && (
                <section
                  className="shell-popover notification-popover"
                  aria-label="Makluman hari ini"
                >
                  <header className="shell-popover__header">
                    <div>
                      <span>Operasi hari ini</span>
                      <h2>Makluman</h2>
                    </div>
                    <span className="shell-popover__date">{dateLabel}</span>
                  </header>
                  <div className="notification-list">
                    {notifications.map((notification) => {
                      const Icon = notification.icon;
                      const content = (
                        <>
                          <span
                            className={`notification-item__icon notification-item__icon--${notification.tone}`}
                            aria-hidden="true"
                          >
                            <Icon size={18} />
                          </span>
                          <span className="notification-item__copy">
                            <strong>{notification.title}</strong>
                            <span>{notification.description}</span>
                          </span>
                        </>
                      );
                      return notification.page ? (
                        <button
                          key={notification.id}
                          type="button"
                          className="notification-item notification-item--action"
                          onClick={() => navigate(notification.page!)}
                        >
                          {content}
                        </button>
                      ) : (
                        <div key={notification.id} className="notification-item">
                          {content}
                        </div>
                      );
                    })}
                  </div>
                </section>
              )}
            </div>

            <div className="topbar-popover-anchor">
              <button
                type="button"
                className="topbar-account"
                aria-label="Buka menu akaun dan peranan"
                aria-expanded={accountOpen}
                onClick={() => {
                  setAccountOpen((open) => !open);
                  setNotificationsOpen(false);
                }}
              >
                <Avatar initials={session.initials} size="sm" label={session.name} />
                <span className="topbar-account__copy">
                  <strong>{session.name}</strong>
                  <span>{session.role === "admin" ? "Admin Sekolah" : "Guru Kelas"}</span>
                </span>
                <ChevronDown size={16} aria-hidden="true" />
              </button>

              {accountOpen && (
                <section className="shell-popover account-popover" aria-label="Menu akaun">
                  <header className="account-popover__identity">
                    <Avatar initials={session.initials} size="md" label={session.name} />
                    <div>
                      <strong>{session.name}</strong>
                      <span>{session.title}</span>
                    </div>
                  </header>
                  <div className="account-popover__role">
                    <span>Peranan semasa</span>
                    <strong>
                      {session.role === "admin" ? "Admin Sekolah" : "Guru Kelas"}
                    </strong>
                  </div>
                  <div className="account-popover__actions">
                    <button type="button" onClick={() => navigate("profile")}>
                      <UserCircle size={18} aria-hidden="true" />
                      Lihat profil
                    </button>
                    <button type="button" onClick={switchRole}>
                      <ArrowRightLeft size={18} aria-hidden="true" />
                      Tukar ke {session.role === "admin" ? "Guru Kelas" : "Admin Sekolah"}
                    </button>
                    <button
                      type="button"
                      className="account-popover__logout"
                      onClick={signOut}
                    >
                      <LogOut size={18} aria-hidden="true" />
                      Keluar dari demo
                    </button>
                  </div>
                </section>
              )}
            </div>
          </div>
        </header>

        <main
          ref={mainRef}
          className="app-main"
          tabIndex={-1}
          aria-label={`${pageLabels[page]} — JosephCare`}
        >
          <div className="app-page">
            {guardNotice && (
              <div className="shell-access-notice" role="status">
                <AlertTriangle size={18} aria-hidden="true" />
                <div>
                  <strong>Akses mengikut peranan</strong>
                  <span>{guardNotice}</span>
                </div>
                <button
                  type="button"
                  aria-label="Tutup makluman akses"
                  onClick={() => setGuardNotice("")}
                >
                  <X size={17} />
                </button>
              </div>
            )}
            {pageContent}
          </div>
        </main>
      </div>

      {session.role === "teacher" && (
        <nav className="mobile-bottom-nav" aria-label="Navigasi mudah alih">
          {mobileNavigationItems.map((item) => {
            const Icon = item.icon;
            const isScan = item.id === "scan";
            return (
              <button
                key={item.id}
                type="button"
                className={`mobile-bottom-nav__item ${
                  isScan ? "mobile-bottom-nav__item--scan" : ""
                }`}
                aria-current={page === item.id ? "page" : undefined}
                onClick={() => navigate(item.id)}
              >
                <span className="mobile-bottom-nav__icon" aria-hidden="true">
                  <Icon size={isScan ? 23 : 20} />
                </span>
                <span>{item.mobileLabel ?? item.label}</span>
              </button>
            );
          })}
        </nav>
      )}
    </div>
  );
}

